import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useApi } from "../lib/useApi";
import type { MergedUser } from "../lib/types";
import { useAuth } from "../lib/auth";
import { formatDate, formatNumber } from "../lib/format";
import { AzureUnavailable, Badge, Card, ConfirmButton, ErrorState, PageHeader, Select, Spinner, Toggle } from "../components/ui";

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 py-1.5 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="text-right font-medium text-slate-800">{children}</span>
    </div>
  );
}

export default function UserDetail() {
  const { email = "" } = useParams();
  const { hasRole } = useAuth();
  const canWrite = hasRole("admin");
  const { data: user, loading, error, reload } = useApi<MergedUser>(`/users/${encodeURIComponent(email)}`);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  async function run(name: string, fn: () => Promise<unknown>) {
    setBusy(name);
    setActionError(null);
    try {
      await fn();
      reload();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  if (loading) return <Spinner />;
  if (error || !user) return <ErrorState message={error ?? "Failed to load"} onRetry={reload} />;

  return (
    <div>
      <PageHeader
        title={user.email}
        subtitle="Merged view — auth worker, free tools, and paid platform"
        actions={
          <Link to="/users" className="text-sm text-rail-600 hover:underline">
            ← All users
          </Link>
        }
      />

      {actionError && <ErrorState message={actionError} />}

      <div className="mt-3 grid gap-4 lg:grid-cols-3">
        <Card title="Auth worker (identity)">
          {user.auth ? (
            <>
              <Row label="Name">{user.auth.name ?? "—"}</Row>
              <Row label="Email verified">{user.auth.email_verified ? <Badge tone="green">yes</Badge> : <Badge tone="amber">no</Badge>}</Row>
              <Row label="Providers">
                <span className="flex gap-1">
                  {user.auth.has_password ? <Badge>password</Badge> : null}
                  {user.auth.has_apple ? <Badge>apple</Badge> : null}
                  {user.auth.has_google ? <Badge>google</Badge> : null}
                </span>
              </Row>
              <Row label="Active sessions">{user.auth.active_sessions}</Row>
              <Row label="Token version">{user.auth.token_version}</Row>
              <Row label="Created">{formatDate(user.auth.created_at)}</Row>
              {canWrite && (
                <div className="mt-3 border-t border-slate-100 pt-3">
                  <ConfirmButton
                    label="Revoke all sessions"
                    confirmLabel="Really revoke all sessions?"
                    disabled={busy !== null}
                    onConfirm={() =>
                      run("revoke", () => api.post(`/users/${encodeURIComponent(user.email)}/revoke-sessions`))
                    }
                  />
                  <p className="mt-1.5 text-xs text-slate-500">
                    Bumps token_version and deletes refresh/exchange tokens on every device.
                  </p>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-slate-500">No auth worker account.</p>
          )}
        </Card>

        <Card title="Free tools">
          {user.freeTools ? (
            <>
              <Row label="Tier">
                {canWrite ? (
                  <Select
                    value={user.freeTools.tier}
                    disabled={busy !== null}
                    onChange={(e) =>
                      run("tier", () =>
                        api.patch(`/users/${encodeURIComponent(user.email)}/free-tools`, { tier: e.target.value }),
                      )
                    }
                  >
                    <option value="free">free</option>
                    <option value="paid">paid</option>
                  </Select>
                ) : (
                  user.freeTools.tier
                )}
              </Row>
              <Row label="Tools admin">
                <Toggle
                  checked={Boolean(user.freeTools.is_admin)}
                  disabled={!canWrite || busy !== null}
                  onChange={(v) =>
                    run("is_admin", () =>
                      api.patch(`/users/${encodeURIComponent(user.email)}/free-tools`, { is_admin: v }),
                    )
                  }
                />
              </Row>
              <Row label="Certificates">{formatNumber(user.freeTools.certificate_count)}</Row>
              <Row label="P6 analyses">{formatNumber(user.freeTools.p6_count)}</Row>
              <Row label="MC simulations">{formatNumber(user.freeTools.mc_count)}</Row>
              <Row label="Signing requests">{formatNumber(user.freeTools.signing_count)}</Row>
              <Row label="Created">{formatDate(user.freeTools.created_at)}</Row>
              {user.signingSettings && (
                <p className="mt-2 text-xs text-slate-500">
                  Has a signing override —{" "}
                  <Link to="/signing" className="text-rail-600 hover:underline">
                    manage on the Signing page
                  </Link>
                  .
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-slate-500">No free tools profile.</p>
          )}
        </Card>

        <Card title="Paid platform (Azure)">
          {user.azureError ? (
            <AzureUnavailable detail={user.azureError} />
          ) : user.azure ? (
            <>
              {Object.entries(user.azure)
                .filter(([k]) => k !== "id")
                .slice(0, 10)
                .map(([k, v]) => (
                  <Row key={k} label={k.replaceAll("_", " ")}>
                    {typeof v === "boolean" ? (v ? "yes" : "no") : String(v ?? "—")}
                  </Row>
                ))}
              {canWrite && "is_active" in user.azure && (
                <div className="mt-3 border-t border-slate-100 pt-3">
                  <ConfirmButton
                    label={user.azure.is_active ? "Disable user" : "Enable user"}
                    confirmLabel="Confirm change?"
                    variant="secondary"
                    disabled={busy !== null}
                    onConfirm={() =>
                      run("azure", () =>
                        api.patch(`/users/${encodeURIComponent(user.email)}/azure`, {
                          azureUserId: (user.azure as { id: string | number }).id,
                          is_active: !(user.azure as { is_active?: boolean }).is_active,
                        }),
                      )
                    }
                  />
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-slate-500">No paid platform account.</p>
          )}
        </Card>
      </div>
    </div>
  );
}
